import {
    AspenCandidatesService,
    AspenCreateCandidatesService,
    CandidateCreateDTO,
    CandidateDTO,
    CandidatePatchDTO,
    PaginatedDTO_CandidateIndexDTO_
} from '../client'
import { PaginatedStateAdapter, usePaginatedData } from './usePaginatedData'

type IndexCandidatesParams = Parameters<typeof AspenCandidatesService.indexCandidatesGet>[0]
export type CandidateStatusFilter = NonNullable<IndexCandidatesParams['status']>[number]

/**
 * ``usePaginatedData`` holds filters as a flat Record<string, string>, so a
 * multi-select status reaches this hook comma-joined rather than as an array.
 * Expand it here, at the one call site that needs it, instead of changing the
 * shared hook's state shape for its six other consumers.
 *
 * The cast is unavoidable: the union of valid statuses cannot survive a trip
 * through a stringly-typed filter map. Values originate from the status
 * dropdown, and the backend rejects anything outside the enum with a 422.
 */
/** Expand a comma-joined filter value back into the array the API expects. */
const asList = (value?: string | string[] | null): string[] | undefined => {
    if (value == null) return undefined
    const list = typeof value === 'string' ? value.split(',').filter(Boolean) : value
    return list.length > 0 ? list : undefined
}

/**
 * `false` and `undefined` mean different things for `hasAppointment`: the first
 * asks for candidates with no booking (the pre-scheduler backlog), the second
 * does not filter at all. The flat filter map can only hold the strings, so the
 * distinction has to be restored here — `Boolean('false')` would be true, which
 * is exactly how a boolean filter silently stops filtering.
 *
 * ``usePaginatedData`` does this for `uncontactedOnly` and
 * `hasPreferredContactTime` itself; this one is candidate-specific, so it is
 * handled here rather than by widening the shared hook for its other consumers.
 */
const asBool = (value?: string | boolean | null): boolean | undefined => {
    if (value === true || value === 'true') return true
    if (value === false || value === 'false') return false
    return undefined
}

const fetchCandidatesPage = ({
    status,
    appointmentStatus,
    hasAppointment,
    ...rest
}: Omit<IndexCandidatesParams, 'status' | 'appointmentStatus' | 'hasAppointment'> & {
    status?: string | string[]
    appointmentStatus?: string | string[]
    hasAppointment?: string | boolean | null
}) =>
    AspenCandidatesService.indexCandidatesGet({
        ...rest,
        status: asList(status) as CandidateStatusFilter[] | undefined,
        // Same comma-joining as `status`, for the same reason: the shared
        // paginated hook stores filters as flat strings (APP-1220).
        appointmentStatus: asList(appointmentStatus),
        hasAppointment: asBool(hasAppointment)
    })

export const useCandidates = (
    initialPage = 1,
    initialLimit = 10,
    initialSort = 'first_name:asc',
    initialFilters = {},
    stateAdapter?: PaginatedStateAdapter
) => {
    const { refetch, ...rest } = usePaginatedData<PaginatedDTO_CandidateIndexDTO_, CandidateCreateDTO, CandidateDTO>(
        fetchCandidatesPage,
        initialPage,
        initialLimit,
        initialSort,
        initialFilters,
        AspenCreateCandidatesService.createCandidatesPost,
        stateAdapter
    )
    return { ...rest, refetch }
}

export const useEnrollCandidate = () => {
    return async (candidateId: string, accepted: boolean) => {
        try {
            await AspenCandidatesService.patchCandidatesCandidateIdPatch({
                candidateId: candidateId,
                requestBody: { id: candidateId, status: accepted ? 'allocated' : 'declined' } as CandidatePatchDTO
            })
        } catch (error) {
            console.error(error)
        }
    }
}
